import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsLabPrinterAPI, settingsInvoicePrinterAPI, printAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../hooks/useNotification';

const Settings = () => {
  const { t, i18n } = useTranslation();
  const { hasAnyRole } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const [activeTab, setActiveTab] = useState('print_lab');
  const [printerSettings, setPrinterSettings] = useState({
    host: '',
    port: '9100',
    width: '384',
    font_size: '24',
    logo_url: '',
    logo_width: '200',
    logo_height: '100',
    logo_fit: 'contain'
  });
  const [labTaskData, setLabTaskData] = useState({
    title: 'قياسات الطلب',
    factory_number: '123',
    order_number: 'SO-999',
    worker_name: 'أحمد',
    length: '100',
    chest: '50',
    waist: '40',
    hips: '60',
    shoulder: '20',
    sleeve: '25',
    neck: '15',
    // Lab Task fields
    lab_status: 'In Progress',
    lab_created_date: new Date().toLocaleDateString('en-US'),
    // Sales Order fields
    sales_customer_name: 'عميل 001',
    sales_order_date: new Date().toLocaleDateString('en-US'),
    sales_delivery_date: '2025-12-20',
    sales_quantity: '10',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [printFields, setPrintFields] = useState({
    // Printer format fields
    show_logo: true,
    show_order_info: true,
    show_measurements: true,
    show_notes: false,
    // Lab Task fields
    show_lab_status: false,
    show_lab_created_date: false,
    // Sales Order fields
    show_sales_delivery_date: false,
    show_sales_customer_name: false,
    show_sales_order_date: false,
    show_sales_quantity: false,
  });

  // Invoice Printer Settings
  const [invoicePrinterSettings, setInvoicePrinterSettings] = useState({
    host: '',
    port: '9100',
    width: '384',
    font_size: '24',
    logo_url: '',
    logo_width: '200',
    logo_height: '100',
    logo_fit: 'contain',
    // Invoice Font Size Settings
    font_scale: '2.0', // Multiplier for all font sizes (increased for better readability)
    // Additional fields
    customer_name: 'ALi',
    discount: '',
    discount_name: '',
    fees: '',
    fees_name: ''
  });
  const [invoiceData, setInvoiceData] = useState({
    // Header
    company_name: 'موستاش جلابية',
    subtitle: '',
    subtitle_ar: '',
    // Invoice info
    invoice_number: '123',
    invoice_date: '2025-12-01',
    // Order info
    order_number: '1',
    location: 'Ajman',
    // Cashier
    cashier_name: 'mohamed',
    // Items
    item_1_qty: '2',
    item_1_name: '٦ اصفار',
    item_1_name_ar: '',
    item_1_price: '30.00',
    item_2_qty: '',
    item_2_name: '',
    item_2_name_ar: '',
    item_2_price: '',
    item_3_qty: '',
    item_3_name: '',
    item_3_name_ar: '',
    item_3_price: '',
    // Summary
    subtotal: '20.00',
    vat_rate: '5',
    vat_amount: '23',
    total: '233',
    // Payment
    amount_paid: '100.00',
    remaining: '133.00',
    // Payment method
    payment_method: 'بطاقه ائتمان',
    payment_amount: '٢٣',
    // Footer
    footer_message: 'شكرًا لزيارتكم',
    footer_message_ar: '',
    // Customer
    customer_phone: '+971501234567',
  });
  const [invoiceLogoFile, setInvoiceLogoFile] = useState(null);
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  const invoiceCanvasRef = useRef(null);
  const invoiceFileInputRef = useRef(null);
  const [invoicePrintFields, setInvoicePrintFields] = useState({
    show_logo: true,
    show_company_info: true,
    show_invoice_number: true,
    show_invoice_date: true,
    show_customer_name: true,
    show_customer_phone: true,
    show_customer_address: true,
    show_item_details: true,
    show_prices: true,
    show_subtotal: true,
    show_tax: true,
    show_total: true,
    show_payment_status: true,
    show_footer_message: true,
  });

  const renderPreview = async (includeLogo = true) => {
    let width = parseInt(printerSettings.width || '384', 10);
    const maxW = 648;
    width = Math.min(Math.max(width, 200), maxW);
    if (width % 8 !== 0) width += (8 - (width % 8));
    const lineH = parseInt(printerSettings.font_size || '24', 10);

    const lines = [];
    const measurementPairs = [];

    if (printFields.show_order_info) {
      lines.push(labTaskData.title);
      lines.push(`رقم المعمل: ${labTaskData.factory_number}`);
      lines.push(`رقم الطلب: ${labTaskData.order_number}`);
      lines.push(`العامل: ${labTaskData.worker_name}`);
    }

    if (printFields.show_measurements) {
      const measurements = [
        { label: 'الطول', value: labTaskData.length },
        { label: 'الصدر', value: labTaskData.chest },
        { label: 'الخصر', value: labTaskData.waist },
        { label: 'الأرداف', value: labTaskData.hips },
        { label: 'الكتف', value: labTaskData.shoulder },
        { label: 'الكم', value: labTaskData.sleeve },
        { label: 'الرقبة', value: labTaskData.neck },
      ];
      // Store measurements for table rendering
      for (let i = 0; i < measurements.length; i++) {
        measurementPairs.push(measurements[i]);
      }
    }

    // Lab Task fields
    if (printFields.show_lab_status) {
      lines.push(`الحالة: ${labTaskData.lab_status}`);
    }
    if (printFields.show_lab_created_date) {
      lines.push(`تاريخ الإنشاء: ${labTaskData.lab_created_date}`);
    }

    // Sales Order fields
    if (printFields.show_sales_customer_name) {
      lines.push(`العميل: ${labTaskData.sales_customer_name}`);
    }
    if (printFields.show_sales_order_date) {
      lines.push(`تاريخ الطلب: ${labTaskData.sales_order_date}`);
    }
    if (printFields.show_sales_delivery_date) {
      lines.push(`تاريخ التسليم: ${labTaskData.sales_delivery_date}`);
    }
    if (printFields.show_sales_quantity) {
      lines.push(`الكمية: ${labTaskData.sales_quantity}`);
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    let logoH = 0;
    let logoW = 0;
    let imgEl = null;

    let logoUrl = logoFile || printerSettings.logo_url;

    // Convert storage URL to API endpoint for CORS support
    if (logoUrl && logoUrl.includes('/storage/logos/') && !logoUrl.startsWith('blob:')) {
      const filename = logoUrl.split('/storage/logos/')[1];
      // Use full backend URL with API endpoint
      logoUrl = `https://moustache-uae.net/api/logo/${filename}`;
    }

    if (includeLogo && logoUrl && printFields.show_logo) {
      try {
        imgEl = await new Promise((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = 'anonymous';
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = logoUrl;
        });

        // Get user-defined logo dimensions
        const targetW = parseInt(printerSettings.logo_width || '200', 10);
        const targetH = parseInt(printerSettings.logo_height || '100', 10);
        const logoFit = printerSettings.logo_fit || 'contain';

        // Calculate actual dimensions based on fit mode
        if (logoFit === 'cover') {
          // Cover: scale to fill the target dimensions, possibly cropping
          const imgRatio = imgEl.width / imgEl.height;
          const targetRatio = targetW / targetH;
          if (imgRatio > targetRatio) {
            logoW = Math.floor(targetH * imgRatio);
            logoH = targetH;
          } else {
            logoW = targetW;
            logoH = Math.floor(targetW / imgRatio);
          }
        } else {
          // Contain: scale to fit within target dimensions
          const imgRatio = imgEl.width / imgEl.height;
          const targetRatio = targetW / targetH;
          if (imgRatio > targetRatio) {
            logoW = targetW;
            logoH = Math.floor(targetW / imgRatio);
          } else {
            logoW = Math.floor(targetH * imgRatio);
            logoH = targetH;
          }
        }
      } catch (err) {
        console.error('Image loading failed:', err);
        imgEl = null;
        logoH = 0;
        logoW = 0;
      }
    }

    const marginTop = 8;
    const marginBottom = 12;
    const padding = Math.floor(lineH / 2);
    const rowH = lineH + 6;
    const height = marginTop + logoH + (imgEl ? padding : 0) + lines.length * rowH + measurementPairs.length * rowH + marginBottom;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `${lineH}px Arial, Tahoma, sans-serif`;
    let y = marginTop;
    if (imgEl && logoW && logoH) {
      const x = Math.floor((width - logoW) / 2);
      ctx.drawImage(imgEl, x, y, logoW, logoH);
      y += logoH + padding;
    }
    const boxMargin = 12;
    const boxW = width - boxMargin * 2;

    // Render order info lines
    lines.forEach((text) => {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.strokeRect(boxMargin, y - 3, boxW, rowH);
      ctx.fillText(text, Math.floor(width / 2), y);
      y += rowH;
    });

    // Render measurements as 2-column table (Label | Value format)
    if (measurementPairs.length > 0) {
      const colW = boxW / 2;
      measurementPairs.forEach((measurement) => {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;

        // Left column - Label
        ctx.strokeRect(boxMargin, y - 3, colW, rowH);
        ctx.textAlign = 'center';
        ctx.fillText(measurement.label, boxMargin + colW / 2, y);

        // Right column - Value
        ctx.strokeRect(boxMargin + colW, y - 3, colW, rowH);
        ctx.fillText(measurement.value, boxMargin + colW + colW / 2, y);

        y += rowH;
      });
    }
  };

  const renderInvoicePreview = async (includeLogo = true) => {
    const canvas = invoiceCanvasRef.current;
    if (!canvas) return;

    let width = parseInt(invoicePrinterSettings.width || '384', 10);
    const maxW = 648;
    width = Math.min(Math.max(width, 200), maxW);
    if (width % 8 !== 0) width += (8 - (width % 8));

    const ctx = canvas.getContext('2d');
    const scale = parseFloat(invoicePrinterSettings.font_scale || '2.0');
    const padding = Math.round(16 * scale);
    const lineHeight = Math.round(24 * scale);
    const margin = Math.round(16 * scale);

    // Detect if RTL (Arabic) language is active
    const isRTL = i18n.language === 'ar' || i18n.language?.startsWith('ar-');

    // Helper to flip text alignment for RTL
    const getAlign = (align) => {
      if (!isRTL) return align;
      if (align === 'left') return 'right';
      if (align === 'right') return 'left';
      return align;
    };

    // Helper to get X position based on RTL
    const getXPos = (align) => {
      const flipped = getAlign(align);
      if (flipped === 'left') return margin;
      if (flipped === 'right') return width - margin;
      return width / 2;
    };

    // Setup
    let y = padding;
    const contentWidth = width - margin * 2;
    const lines = [];

    // Helper function to scale fonts
    const scaledFont = (baseSize) => Math.round(baseSize * scale);

    const drawLine = (text, size = 10, bold = false, align = 'center') => {
      ctx.font = `${bold ? 'bold' : ''} ${scaledFont(size)}px Arial, sans-serif`;
      ctx.textAlign = align;
      ctx.fillText(text, align === 'left' ? margin : width / 2, y);
      y += lineHeight;
    };

    const drawHLine = () => {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(margin, y + 5);
      ctx.lineTo(width - margin, y + 5);
      ctx.stroke();
      y += lineHeight;
    };

    const drawItemRow = (qty, item, price) => {
      ctx.font = `${scaledFont(15)}px Arial, sans-serif`;
      ctx.textAlign = getAlign('left');
      ctx.fillText(qty, getXPos('left'), y);
      ctx.fillText(item, isRTL ? width - margin - Math.round(50 * scale) : margin + Math.round(50 * scale), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(price, getXPos('right'), y);
      y += lineHeight;
    };

    // Calculate total height first
    let tempY = y;

    // Header
    tempY += lineHeight * 2; // Company name
    tempY += lineHeight; // Subtitle
    tempY += lineHeight * 2; // Invoice number, date
    tempY += lineHeight; // Separator
    tempY += lineHeight * 2; // Order info
    tempY += lineHeight; // Cashier
    tempY += lineHeight; // Separator
    tempY += lineHeight * 5; // Items (assume 3 items)
    tempY += lineHeight; // Separator
    tempY += lineHeight * 4; // Summary
    tempY += lineHeight; // Separator
    tempY += lineHeight * 2; // Payment status (amount paid, remaining)
    tempY += lineHeight; // Separator
    tempY += lineHeight * 2; // Payment method (1 line instead of 2)
    tempY += lineHeight; // Separator
    tempY += lineHeight * 2; // Footer
    tempY += lineHeight; // Customer info

    const height = tempY + padding;
    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';
    y = padding;

    // Logo
    let logoUrl = invoiceLogoFile || invoicePrinterSettings.logo_url;
    if (logoUrl && logoUrl.includes('/storage/logos/') && !logoUrl.startsWith('blob:')) {
      const filename = logoUrl.split('/storage/logos/')[1];
      logoUrl = `https://moustache-uae.net/api/logo/${filename}`;
    }

    if (includeLogo && logoUrl && invoicePrintFields.show_logo) {
      try {
        const imgEl = await new Promise((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = 'anonymous';
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = logoUrl;
        });

        const logoW = 60;
        const logoH = 30;
        const logoX = (width - logoW) / 2;
        ctx.drawImage(imgEl, logoX, y, logoW, logoH);
        y += logoH + 5;
      } catch (err) {
        console.error('Logo loading failed:', err);
      }
    }

    // Company name and subtitle
    ctx.font = `bold ${scaledFont(28)}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(invoiceData.company_name, width / 2, y);
    y += lineHeight;

    ctx.font = `${scaledFont(16)}px Arial, sans-serif`;
    ctx.fillText(invoiceData.subtitle, width / 2, y);
    y += lineHeight * 0.8;
    ctx.fillText(invoiceData.subtitle_ar, width / 2, y);
    y += lineHeight;

    // Invoice number and date
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.fillText(`Invoice Number - رقم الفاتورة - ${invoiceData.invoice_number}`, width / 2, y);
    y += lineHeight;
    ctx.fillText(invoiceData.invoice_date, width / 2, y);
    y += lineHeight;

    // Order info
    drawHLine();
    ctx.font = `bold ${scaledFont(16)}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText(`Order # ${invoiceData.order_number}`, margin, y);
    y += lineHeight;
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.fillText(invoiceData.location, margin, y);
    y += lineHeight + 3;

    // Two columns - Takeaway/Store labels
    ctx.textAlign = 'left';
    ctx.fillText(invoiceData.location, margin, y);
    ctx.textAlign = 'right';
    ctx.fillText('Store', width - margin, y);
    y += lineHeight + 3;

    // Cashier
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText(`Cashier: ${invoiceData.cashier_name}`, margin, y);
    y += lineHeight;

    // Items header
    drawHLine();
    ctx.font = `bold ${scaledFont(15)}px Arial, sans-serif`;
    ctx.textAlign = getAlign('left');
    ctx.fillText('Qty', getXPos('left'), y);
    ctx.fillText('Item', isRTL ? width - margin - Math.round(50 * scale) : margin + Math.round(50 * scale), y);
    ctx.textAlign = getAlign('right');
    ctx.fillText('Total', getXPos('right'), y);
    y += lineHeight;

    // Items
    if (invoicePrintFields.show_item_details) {
      drawItemRow(invoiceData.item_1_qty, invoiceData.item_1_name, invoiceData.item_1_price);
      drawItemRow(invoiceData.item_2_qty, invoiceData.item_2_name, invoiceData.item_2_price);
      drawItemRow(invoiceData.item_3_qty, invoiceData.item_3_name, invoiceData.item_3_price);
    }

    // Summary
    drawHLine();
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.textAlign = getAlign('left');
    ctx.fillText('Total Inclusive of VAT', getXPos('left'), y);
    ctx.textAlign = getAlign('right');
    ctx.fillText(invoiceData.total, getXPos('right'), y);
    y += lineHeight;

    ctx.textAlign = getAlign('left');
    ctx.fillText(`VAT ${invoiceData.vat_rate}%`, getXPos('left'), y);
    ctx.textAlign = getAlign('right');
    ctx.fillText(invoiceData.vat_amount, getXPos('right'), y);
    y += lineHeight;

    ctx.font = `${scaledFont(13)}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('الأسعار شامل ضريبة القيمة المضافة', width / 2, y);
    y += lineHeight;
    ctx.fillText('Prices are VAT inclusive', width / 2, y);
    y += lineHeight;

    // Amount Paid
    if (invoiceData.amount_paid && parseFloat(invoiceData.amount_paid) > 0) {
      ctx.font = `${scaledFont(12)}px Arial, sans-serif`;
      ctx.textAlign = getAlign('left');
      ctx.fillText('Amount Paid', getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(invoiceData.amount_paid, getXPos('right'), y);
      y += lineHeight;
    }

    // Remaining Amount
    if (invoiceData.remaining && parseFloat(invoiceData.remaining) > 0) {
      ctx.font = `bold ${scaledFont(14)}px Arial, sans-serif`;
      ctx.fillStyle = '#FF6B6B';
      ctx.textAlign = getAlign('left');
      ctx.fillText('Remaining Amount', getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(invoiceData.remaining, getXPos('right'), y);
      ctx.fillStyle = '#000000';
      y += lineHeight;
    }

    // Payment method
    drawHLine();
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText(`طريقة الدفع`, margin, y);
    y += lineHeight + 2;

    ctx.font = `${scaledFont(15)}px Arial, sans-serif`;
    ctx.fillText(invoiceData.payment_method, margin, y);
    ctx.textAlign = 'right';
    ctx.fillText(invoiceData.payment_amount, width - margin, y);
    y += lineHeight;

    // Footer
    drawHLine();
    ctx.font = `bold ${scaledFont(18)}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(invoiceData.footer_message, width / 2, y);
    y += lineHeight;
    ctx.fillText(invoiceData.footer_message_ar, width / 2, y);
    y += lineHeight;

    // Customer info
    ctx.font = `${scaledFont(13)}px Arial, sans-serif`;
    ctx.fillText('Customer Info', width / 2, y);
    y += lineHeight;
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.fillText(invoiceData.customer_phone, width / 2, y);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await settingsLabPrinterAPI.get();
        const settings = res.data.data || {};
        setPrinterSettings({
          host: settings.host || '',
          port: settings.port || '9100',
          width: settings.width || '384',
          font_size: settings.font_size || '24',
          logo_url: settings.logo_url || '',
          logo_width: settings.logo_width || '200',
          logo_height: settings.logo_height || '100',
          logo_fit: settings.logo_fit || 'contain'
        });
        setPrintFields({
          show_logo: settings.show_logo !== false,
          show_order_info: settings.show_order_info !== false,
          show_measurements: settings.show_measurements !== false,
          show_notes: settings.show_notes === true,
          show_lab_status: settings.show_lab_status === true,
          show_lab_created_date: settings.show_lab_created_date === true,
          show_sales_delivery_date: settings.show_sales_delivery_date === true,
          show_sales_customer_name: settings.show_sales_customer_name === true,
          show_sales_order_date: settings.show_sales_order_date === true,
          show_sales_quantity: settings.show_sales_quantity === true,
        });
      } catch (_) {}
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    renderPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printerSettings.width, printerSettings.font_size, printerSettings.logo_url, printerSettings.logo_width, printerSettings.logo_height, printerSettings.logo_fit, logoFile, printFields]);

  useEffect(() => {
    if (!loading) {
      renderPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    renderPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labTaskData, printFields]);

  // Load invoice printer settings
  useEffect(() => {
    const loadInvoiceSettings = async () => {
      try {
        const res = await settingsInvoicePrinterAPI.get();
        const settings = res.data.data || {};
        setInvoicePrinterSettings({
          host: settings.host || '',
          port: settings.port || '9100',
          width: settings.width || '384',
          font_size: settings.font_size || '24',
          font_scale: settings.font_scale || '2.0',
          logo_url: settings.logo_url || '',
          logo_width: settings.logo_width || '200',
          logo_height: settings.logo_height || '100',
          logo_fit: settings.logo_fit || 'contain',
          customer_name: settings.customer_name || '',
          discount: settings.discount || '',
          discount_name: settings.discount_name || '',
          fees: settings.fees || '',
          fees_name: settings.fees_name || ''
        });
        setInvoicePrintFields({
          show_logo: settings.show_logo !== false,
          show_company_info: settings.show_company_info !== false,
          show_invoice_number: settings.show_invoice_number !== false,
          show_invoice_date: settings.show_invoice_date !== false,
          show_customer_name: settings.show_customer_name !== false,
          show_customer_phone: settings.show_customer_phone !== false,
          show_customer_address: settings.show_customer_address !== false,
          show_item_details: settings.show_item_details !== false,
          show_prices: settings.show_prices !== false,
          show_subtotal: settings.show_subtotal !== false,
          show_tax: settings.show_tax !== false,
          show_total: settings.show_total !== false,
          show_payment_status: settings.show_payment_status !== false,
          show_footer_message: settings.show_footer_message !== false,
        });
      } catch (_) {}
    };
    loadInvoiceSettings();
  }, []);

  // Render invoice preview when settings or data changes
  useEffect(() => {
    renderInvoicePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePrinterSettings.width, invoicePrinterSettings.font_size, invoicePrinterSettings.font_scale, invoicePrinterSettings.logo_url, invoicePrinterSettings.logo_width, invoicePrinterSettings.logo_height, invoicePrinterSettings.logo_fit, invoiceLogoFile, invoicePrintFields]);

  useEffect(() => {
    renderInvoicePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData, invoicePrintFields]);

  const savePrinterSettings = async () => {
    setSavingSettings(true);
    try {
      const data = {
        // Printer connection & format
        host: printerSettings.host,
        port: printerSettings.port,
        width: printerSettings.width,
        font_size: printerSettings.font_size,
        logo_url: printerSettings.logo_url,
        logo_width: printerSettings.logo_width,
        logo_height: printerSettings.logo_height,
        logo_fit: printerSettings.logo_fit,
        // Print field toggles
        show_logo: printFields.show_logo,
        show_order_info: printFields.show_order_info,
        show_measurements: printFields.show_measurements,
        show_notes: printFields.show_notes,
        // Lab task fields
        show_lab_status: printFields.show_lab_status,
        show_lab_created_date: printFields.show_lab_created_date,
        // Sales order fields
        show_sales_delivery_date: printFields.show_sales_delivery_date,
        show_sales_customer_name: printFields.show_sales_customer_name,
        show_sales_order_date: printFields.show_sales_order_date,
        show_sales_quantity: printFields.show_sales_quantity,
      };
      await settingsLabPrinterAPI.update(data);
      showSuccess(t('common.success'));
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    setLogoFile(localUrl);

    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await settingsLabPrinterAPI.uploadLogo(fd);
      console.log('Logo upload response:', res.data);
      const fullUrl = res.data.url;
      console.log('Full URL from backend:', fullUrl);

      // Extract relative path from full URL to store in database
      // The backend returns full URL but stores relative path, so we need to extract /storage/logos/...
      let relativeUrl = fullUrl;
      if (fullUrl.includes('/storage/')) {
        // Extract everything from '/storage/' onwards
        relativeUrl = fullUrl.substring(fullUrl.indexOf('/storage/'));
        console.log('Extracted relative path:', relativeUrl);
      }

      // Use full URL for local display/preview
      setPrinterSettings(ps => ({ ...ps, logo_url: fullUrl }));
      console.log('Updated printerSettings with full URL:', fullUrl);

      // Store relative path in database
      const updateRes = await settingsLabPrinterAPI.update({ logo_url: relativeUrl });
      console.log('Settings update response:', updateRes.data);
      setLogoFile(null);
      showSuccess('Logo uploaded successfully');
    } catch (err) {
      console.error('Logo upload error:', err);
      showError('Failed to upload logo');
      setLogoFile(null);
    }
  };

  const handleTestPrint = async () => {
    const width = parseInt(printerSettings.width || '384', 10);
    if (!printerSettings.host || !printerSettings.port) {
      showWarning('Set Printer IP and Port first');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let img;
    try {
      img = ctx.getImageData(0, 0, width, canvas.height);
    } catch (e) {
      await renderPreview(false);
      img = ctx.getImageData(0, 0, width, canvas.height);
    }
    const wBytes = Math.ceil(width / 8);
    const xL = wBytes & 0xFF;
    const xH = (wBytes >> 8) & 0xFF;
    const yL = canvas.height & 0xFF;
    const yH = (canvas.height >> 8) & 0xFF;
    const header = [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH];
    const data = new Uint8Array(header.length + wBytes * canvas.height);
    for (let i = 0; i < header.length; i++) data[i] = header[i];
    let offset = header.length;
    for (let row = 0; row < canvas.height; row++) {
      for (let byte = 0; byte < wBytes; byte++) {
        let b = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = byte * 8 + bit;
          if (x >= width) continue;
          const idx = (row * width + x) * 4;
          const r = img.data[idx];
          const g = img.data[idx + 1];
          const bl = img.data[idx + 2];
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
          const isBlack = luminance < 160;
          b |= (isBlack ? 1 : 0) << (7 - bit);
        }
        data[offset++] = b;
      }
    }
    const b64 = btoa(String.fromCharCode.apply(null, Array.from(data)));
    try {
      await printAPI.test({
        printer_ip: printerSettings.host,
        printer_port: parseInt(printerSettings.port || '9100', 10),
        data_base64: b64,
      });
      showSuccess(t('common.success'));
    } catch (err) {
      const msg = err.response?.data?.message || t('messages.error');
      const detail = err.response?.data?.error || '';
      showError(`${msg}${detail ? ' - ' + detail : ''}`);
    }
  };

  const saveInvoicePrinterSettings = async () => {
    setSavingInvoiceSettings(true);
    try {
      const data = {
        host: invoicePrinterSettings.host,
        port: invoicePrinterSettings.port,
        width: invoicePrinterSettings.width,
        font_size: invoicePrinterSettings.font_size,
        font_scale: invoicePrinterSettings.font_scale,
        logo_url: invoicePrinterSettings.logo_url,
        logo_width: invoicePrinterSettings.logo_width,
        logo_height: invoicePrinterSettings.logo_height,
        logo_fit: invoicePrinterSettings.logo_fit,
        customer_name: invoicePrinterSettings.customer_name,
        discount: invoicePrinterSettings.discount,
        discount_name: invoicePrinterSettings.discount_name,
        fees: invoicePrinterSettings.fees,
        fees_name: invoicePrinterSettings.fees_name,
        show_logo: invoicePrintFields.show_logo,
        show_company_info: invoicePrintFields.show_company_info,
        show_invoice_number: invoicePrintFields.show_invoice_number,
        show_invoice_date: invoicePrintFields.show_invoice_date,
        show_customer_name: invoicePrintFields.show_customer_name,
        show_customer_phone: invoicePrintFields.show_customer_phone,
        show_customer_address: invoicePrintFields.show_customer_address,
        show_item_details: invoicePrintFields.show_item_details,
        show_prices: invoicePrintFields.show_prices,
        show_subtotal: invoicePrintFields.show_subtotal,
        show_tax: invoicePrintFields.show_tax,
        show_total: invoicePrintFields.show_total,
        show_payment_status: invoicePrintFields.show_payment_status,
        show_footer_message: invoicePrintFields.show_footer_message,
      };
      await settingsInvoicePrinterAPI.update(data);
      showSuccess(t('common.success'));
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  const handleInvoiceLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    setInvoiceLogoFile(localUrl);

    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await settingsInvoicePrinterAPI.uploadLogo(fd);
      const fullUrl = res.data.url;

      let relativeUrl = fullUrl;
      if (fullUrl.includes('/storage/')) {
        relativeUrl = fullUrl.substring(fullUrl.indexOf('/storage/'));
      }

      setInvoicePrinterSettings(ps => ({ ...ps, logo_url: fullUrl }));

      const updateRes = await settingsInvoicePrinterAPI.update({ logo_url: relativeUrl });
      setInvoiceLogoFile(null);
      showSuccess('Logo uploaded successfully');
    } catch (err) {
      console.error('Logo upload error:', err);
      showError('Failed to upload logo');
      setInvoiceLogoFile(null);
    }
  };

  const handleInvoiceTestPrint = async () => {
    const width = parseInt(invoicePrinterSettings.width || '384', 10);
    if (!invoicePrinterSettings.host || !invoicePrinterSettings.port) {
      showWarning('Set Printer IP and Port first');
      return;
    }
    const canvas = invoiceCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let img;
    try {
      img = ctx.getImageData(0, 0, width, canvas.height);
    } catch (e) {
      await renderInvoicePreview(false);
      img = ctx.getImageData(0, 0, width, canvas.height);
    }
    const wBytes = Math.ceil(width / 8);
    const xL = wBytes & 0xFF;
    const xH = (wBytes >> 8) & 0xFF;
    const yL = canvas.height & 0xFF;
    const yH = (canvas.height >> 8) & 0xFF;
    const header = [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH];
    const data = new Uint8Array(header.length + wBytes * canvas.height);
    for (let i = 0; i < header.length; i++) data[i] = header[i];
    let offset = header.length;
    for (let row = 0; row < canvas.height; row++) {
      for (let byte = 0; byte < wBytes; byte++) {
        let b = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = byte * 8 + bit;
          if (x >= width) continue;
          const idx = (row * width + x) * 4;
          const r = img.data[idx];
          const g = img.data[idx + 1];
          const bl = img.data[idx + 2];
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
          const isBlack = luminance < 160;
          b |= (isBlack ? 1 : 0) << (7 - bit);
        }
        data[offset++] = b;
      }
    }
    const b64 = btoa(String.fromCharCode.apply(null, Array.from(data)));
    try {
      await printAPI.test({
        printer_ip: invoicePrinterSettings.host,
        printer_port: parseInt(invoicePrinterSettings.port || '9100', 10),
        data_base64: b64,
      });
      showSuccess(t('common.success'));
    } catch (err) {
      const msg = err.response?.data?.message || t('messages.error');
      const detail = err.response?.data?.error || '';
      showError(`${msg}${detail ? ' - ' + detail : ''}`);
    }
  };

  if (!hasAnyRole(['Admin', 'Lab', 'Sales'])) {
    return <div className="p-6">{t('common.unauthorized') || 'Unauthorized'}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
        <div className="flex items-center gap-3 mb-4">
          <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Printer Settings</h1>
            <p className="text-sm text-gray-600">Configure your lab printer and preview output</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex gap-2 border-t border-gray-200 pt-4">
          <button
            onClick={() => setActiveTab('print_lab')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'print_lab'
                ? 'bg-yellow-500 text-gray-900 shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
              </svg>
              Print Setting Lab
            </span>
          </button>
          <button
            onClick={() => setActiveTab('print_invoice')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'print_invoice'
                ? 'bg-yellow-500 text-gray-900 shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Print Invoice Setting
            </span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-64 bg-gray-200 rounded-xl"></div>
          <div className="h-40 bg-gray-200 rounded-xl"></div>
        </div>
      ) : activeTab === 'print_lab' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Printer Connection */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-yellow-500">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Connection</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Printer IP Address</label>
                  <input
                    type="text"
                    placeholder="192.168.1.100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={printerSettings.host}
                    onChange={e => setPrinterSettings(ps => ({...ps, host: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Port Number</label>
                  <input
                    type="text"
                    placeholder="9100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={printerSettings.port}
                    onChange={e => setPrinterSettings(ps => ({...ps, port: e.target.value}))}
                  />
                </div>
              </div>
            </div>

            {/* Paper Settings */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-gray-400">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Paper Format</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Paper Width (px)</label>
                  <input
                    type="text"
                    placeholder="384"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={printerSettings.width}
                    onChange={e => setPrinterSettings(ps => ({...ps, width: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Font Size (px)</label>
                  <input
                    type="text"
                    placeholder="24"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={printerSettings.font_size}
                    onChange={e => setPrinterSettings(ps => ({...ps, font_size: e.target.value}))}
                  />
                </div>
              </div>
            </div>

            {/* Logo */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-yellow-500">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Logo</h3>
              </div>
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="block w-full text-sm text-gray-500
                    file:mr-3 file:py-2 file:px-3 file:rounded-lg
                    file:border-0 file:text-sm file:font-semibold
                    file:bg-yellow-100 file:text-yellow-700
                    hover:file:bg-yellow-200"
                />
                {printerSettings.logo_url && (
                  <div className="text-xs text-green-600 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Logo uploaded
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-3 font-semibold">Logo Sizing</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Width (px)</label>
                      <input
                        type="number"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        value={printerSettings.logo_width}
                        onChange={e => setPrinterSettings(ps => ({...ps, logo_width: e.target.value}))}
                        min="50"
                        max="500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Height (px)</label>
                      <input
                        type="number"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        value={printerSettings.logo_height}
                        onChange={e => setPrinterSettings(ps => ({...ps, logo_height: e.target.value}))}
                        min="50"
                        max="500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Scaling Mode</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={printerSettings.logo_fit}
                      onChange={e => setPrinterSettings(ps => ({...ps, logo_fit: e.target.value}))}
                    >
                      <option value="contain">Contain (fit inside bounds)</option>
                      <option value="cover">Cover (fill bounds with crop)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button
                disabled={savingSettings}
                onClick={savePrinterSettings}
                className="w-full px-4 py-3 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 transition-all font-bold text-sm disabled:opacity-50 disabled:bg-gray-400"
              >
                {savingSettings ? 'Saving…' : 'Save Settings'}
              </button>
              <button
                onClick={handleTestPrint}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all font-bold text-sm"
              >
                Test Print
              </button>
            </div>
          </div>

          {/* WYSIWYG Editor Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Task Data Editor */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-yellow-500">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Lab Task Data
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={labTaskData.title}
                    onChange={e => setLabTaskData(d => ({...d, title: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Factory #</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={labTaskData.factory_number}
                    onChange={e => setLabTaskData(d => ({...d, factory_number: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Order #</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={labTaskData.order_number}
                    onChange={e => setLabTaskData(d => ({...d, order_number: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Worker</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    value={labTaskData.worker_name}
                    onChange={e => setLabTaskData(d => ({...d, worker_name: e.target.value}))}
                  />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Measurements</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { key: 'length', label: 'Length' },
                    { key: 'chest', label: 'Chest' },
                    { key: 'waist', label: 'Waist' },
                    { key: 'hips', label: 'Hips' },
                    { key: 'shoulder', label: 'Shoulder' },
                    { key: 'sleeve', label: 'Sleeve' },
                    { key: 'neck', label: 'Neck' },
                  ].map(m => (
                    <div key={m.key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{m.label}</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        value={labTaskData[m.key]}
                        onChange={e => setLabTaskData(d => ({...d, [m.key]: e.target.value}))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Lab Task Data</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <input
                      type="text"
                      placeholder="In Progress"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={labTaskData.lab_status}
                      onChange={e => setLabTaskData(d => ({...d, lab_status: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Created Date</label>
                    <input
                      type="text"
                      placeholder="11/18/2025"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={labTaskData.lab_created_date}
                      onChange={e => setLabTaskData(d => ({...d, lab_created_date: e.target.value}))}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Sales Order Data</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Customer Name</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={labTaskData.sales_customer_name}
                      onChange={e => setLabTaskData(d => ({...d, sales_customer_name: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Order Date</label>
                    <input
                      type="text"
                      placeholder="11/18/2025"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={labTaskData.sales_order_date}
                      onChange={e => setLabTaskData(d => ({...d, sales_order_date: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Date</label>
                    <input
                      type="text"
                      placeholder="2025-12-20"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={labTaskData.sales_delivery_date}
                      onChange={e => setLabTaskData(d => ({...d, sales_delivery_date: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      value={labTaskData.sales_quantity}
                      onChange={e => setLabTaskData(d => ({...d, sales_quantity: e.target.value}))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Print Fields Selector */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-yellow-500">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                Print Fields
              </h3>
              <p className="text-xs text-gray-600 mb-4">Select which sections to include in printed output:</p>

              {/* Format Fields */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Format</p>
                <div className="space-y-2">
                  {[
                    { key: 'show_logo', label: 'Logo' },
                    { key: 'show_order_info', label: 'Order Info' },
                    { key: 'show_measurements', label: 'Measurements' },
                    { key: 'show_notes', label: 'Notes/Comments' },
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-yellow-50">
                      <input
                        type="checkbox"
                        checked={printFields[field.key]}
                        onChange={e => setPrintFields(pf => ({...pf, [field.key]: e.target.checked}))}
                        className="w-4 h-4 text-yellow-500 rounded focus:ring-2 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Lab Task Fields */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Lab Task</p>
                <div className="space-y-2">
                  {[
                    { key: 'show_lab_status', label: 'Status' },
                    { key: 'show_lab_created_date', label: 'Created Date' },
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-yellow-50">
                      <input
                        type="checkbox"
                        checked={printFields[field.key]}
                        onChange={e => setPrintFields(pf => ({...pf, [field.key]: e.target.checked}))}
                        className="w-4 h-4 text-yellow-500 rounded focus:ring-2 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sales Order Fields */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Sales Order</p>
                <div className="space-y-2">
                  {[
                    { key: 'show_sales_customer_name', label: 'Customer Name' },
                    { key: 'show_sales_order_date', label: 'Order Date' },
                    { key: 'show_sales_delivery_date', label: 'Delivery Date' },
                    { key: 'show_sales_quantity', label: 'Quantity/Pieces' },
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-yellow-50">
                      <input
                        type="checkbox"
                        checked={printFields[field.key]}
                        onChange={e => setPrintFields(pf => ({...pf, [field.key]: e.target.checked}))}
                        className="w-4 h-4 text-yellow-500 rounded focus:ring-2 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="bg-white rounded-xl shadow-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                Live Preview
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 overflow-x-auto">
                <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto' }} className="mx-auto" />
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Changes are reflected in real-time as you edit
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Settings Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Printer Connection */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-blue-500">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Connection</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Printer IP Address</label>
                  <input
                    type="text"
                    placeholder="192.168.1.100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={invoicePrinterSettings.host}
                    onChange={e => setInvoicePrinterSettings(ps => ({...ps, host: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Port Number</label>
                  <input
                    type="text"
                    placeholder="9100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={invoicePrinterSettings.port}
                    onChange={e => setInvoicePrinterSettings(ps => ({...ps, port: e.target.value}))}
                  />
                </div>
              </div>
            </div>

            {/* Paper Settings */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-gray-400">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Paper Format</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Paper Width (px)</label>
                  <input
                    type="text"
                    placeholder="384"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={invoicePrinterSettings.width}
                    onChange={e => setInvoicePrinterSettings(ps => ({...ps, width: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Font Size (px)</label>
                  <input
                    type="text"
                    placeholder="24"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={invoicePrinterSettings.font_size}
                    onChange={e => setInvoicePrinterSettings(ps => ({...ps, font_size: e.target.value}))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Font Size Scale Multiplier</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      className="flex-1 h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                      value={invoicePrinterSettings.font_scale}
                      onChange={e => setInvoicePrinterSettings(ps => ({...ps, font_scale: e.target.value}))}
                    />
                    <span className="text-sm font-semibold text-blue-600 w-12 text-center">{invoicePrinterSettings.font_scale}x</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Adjust text size: 0.5x (very small) to 3x (very large)</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Customer Name"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={invoicePrinterSettings.customer_name}
                    onChange={e => setInvoicePrinterSettings(ps => ({...ps, customer_name: e.target.value}))}
                  />
                </div>
              </div>
            </div>

            {/* Discount & Fees Settings */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-green-500">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v-4.721a2 2 0 00-.567.267m0 0C7.328 7.284 6.335 8 5.891 8H5a2 2 0 002 2h.111a7 7 0 007.712 0h.111a2 2 0 002-2h-.891c-.444 0-1.437-.716-1.833-1.414m-4.296 0a2.573 2.573 0 00-4.142 0c.155.103.346.196.567.267m4.1-.78L5.75 4.75a2 2 0 00-1.5.75m0 0L3.5 6a2 2 0 002 2h.011a7 7 0 007.712 0H13a2 2 0 002-2l-2.75-2.75a2 2 0 00-1.5-.75m0 0a2.573 2.573 0 00-4.142 0m5.25 4.75H5" />
                </svg>
                <h3 className="font-semibold text-gray-900">Discount & Fees</h3>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Discount Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Discount"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={invoicePrinterSettings.discount_name}
                      onChange={e => setInvoicePrinterSettings(ps => ({...ps, discount_name: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Discount Amount</label>
                    <input
                      type="text"
                      placeholder="e.g. 0.00"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={invoicePrinterSettings.discount}
                      onChange={e => setInvoicePrinterSettings(ps => ({...ps, discount: e.target.value}))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fees Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Service Fee"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={invoicePrinterSettings.fees_name}
                      onChange={e => setInvoicePrinterSettings(ps => ({...ps, fees_name: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fees Amount</label>
                    <input
                      type="text"
                      placeholder="e.g. 0.00"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={invoicePrinterSettings.fees}
                      onChange={e => setInvoicePrinterSettings(ps => ({...ps, fees: e.target.value}))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Logo */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-blue-500">
              <div className="flex items-center gap-2 mb-4">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Logo</h3>
              </div>
              <div className="space-y-3">
                <input
                  ref={invoiceFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleInvoiceLogoUpload}
                  className="block w-full text-sm text-gray-500
                    file:mr-3 file:py-2 file:px-3 file:rounded-lg
                    file:border-0 file:text-sm file:font-semibold
                    file:bg-blue-100 file:text-blue-700
                    hover:file:bg-blue-200"
                />
                {invoicePrinterSettings.logo_url && (
                  <div className="text-xs text-green-600 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Logo uploaded
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-3 font-semibold">Logo Sizing</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Width (px)</label>
                      <input
                        type="number"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={invoicePrinterSettings.logo_width}
                        onChange={e => setInvoicePrinterSettings(ps => ({...ps, logo_width: e.target.value}))}
                        min="50"
                        max="500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Height (px)</label>
                      <input
                        type="number"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={invoicePrinterSettings.logo_height}
                        onChange={e => setInvoicePrinterSettings(ps => ({...ps, logo_height: e.target.value}))}
                        min="50"
                        max="500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Scaling Mode</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={invoicePrinterSettings.logo_fit}
                      onChange={e => setInvoicePrinterSettings(ps => ({...ps, logo_fit: e.target.value}))}
                    >
                      <option value="contain">Contain (fit inside bounds)</option>
                      <option value="cover">Cover (fill bounds with crop)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button
                disabled={savingInvoiceSettings}
                onClick={saveInvoicePrinterSettings}
                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all font-bold text-sm disabled:opacity-50 disabled:bg-gray-400"
              >
                {savingInvoiceSettings ? 'Saving…' : 'Save Settings'}
              </button>
              <button
                onClick={handleInvoiceTestPrint}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all font-bold text-sm"
              >
                Test Print
              </button>
            </div>
          </div>

          {/* Invoice Preview Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Data Editor */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-blue-500 overflow-y-auto max-h-96">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Invoice Data (POS Receipt)
              </h3>

              {/* Header */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Header</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Company Name</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.company_name} onChange={e => setInvoiceData(d => ({...d, company_name: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Subtitle</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.subtitle} onChange={e => setInvoiceData(d => ({...d, subtitle: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Subtitle AR</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.subtitle_ar} onChange={e => setInvoiceData(d => ({...d, subtitle_ar: e.target.value}))} /></div>
                </div>
              </div>

              {/* Invoice Info */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Invoice Info</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Invoice #</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.invoice_number} onChange={e => setInvoiceData(d => ({...d, invoice_number: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Date</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.invoice_date} onChange={e => setInvoiceData(d => ({...d, invoice_date: e.target.value}))} /></div>
                </div>
              </div>

              {/* Order Info */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Order Info</p>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Order #</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.order_number} onChange={e => setInvoiceData(d => ({...d, order_number: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Location</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.location} onChange={e => setInvoiceData(d => ({...d, location: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Cashier</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.cashier_name} onChange={e => setInvoiceData(d => ({...d, cashier_name: e.target.value}))} /></div>
                </div>
              </div>

              {/* Items */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Items (3 max)</p>
                {[1,2,3].map(i => (
                  <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                    <div><input type="text" placeholder="Qty" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData[`item_${i}_qty`]} onChange={e => setInvoiceData(d => ({...d, [`item_${i}_qty`]: e.target.value}))} /></div>
                    <div><input type="text" placeholder="Name" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData[`item_${i}_name`]} onChange={e => setInvoiceData(d => ({...d, [`item_${i}_name`]: e.target.value}))} /></div>
                    <div><input type="text" placeholder="AR" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData[`item_${i}_name_ar`]} onChange={e => setInvoiceData(d => ({...d, [`item_${i}_name_ar`]: e.target.value}))} /></div>
                    <div><input type="text" placeholder="Price" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData[`item_${i}_price`]} onChange={e => setInvoiceData(d => ({...d, [`item_${i}_price`]: e.target.value}))} /></div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Summary</p>
                <div className="grid grid-cols-4 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Subtotal</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.subtotal} onChange={e => setInvoiceData(d => ({...d, subtotal: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">VAT %</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.vat_rate} onChange={e => setInvoiceData(d => ({...d, vat_rate: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">VAT Amt</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.vat_amount} onChange={e => setInvoiceData(d => ({...d, vat_amount: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Total</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.total} onChange={e => setInvoiceData(d => ({...d, total: e.target.value}))} /></div>
                </div>
              </div>

              {/* Payment Status */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Payment Status</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Amount Paid</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.amount_paid} onChange={e => setInvoiceData(d => ({...d, amount_paid: e.target.value}))} placeholder="e.g. 100.00" /></div>
                  <div><label className="text-xs font-medium text-gray-600">Remaining</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.remaining} onChange={e => setInvoiceData(d => ({...d, remaining: e.target.value}))} placeholder="e.g. 133.00" /></div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Payment Method</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Method (AR)</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.payment_method} onChange={e => setInvoiceData(d => ({...d, payment_method: e.target.value}))} placeholder="e.g. دفع" /></div>
                  <div><label className="text-xs font-medium text-gray-600">Amount</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.payment_amount} onChange={e => setInvoiceData(d => ({...d, payment_amount: e.target.value}))} /></div>
                </div>
              </div>

              {/* Footer */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Footer</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs font-medium text-gray-600">Message EN</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.footer_message} onChange={e => setInvoiceData(d => ({...d, footer_message: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium text-gray-600">Message AR</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.footer_message_ar} onChange={e => setInvoiceData(d => ({...d, footer_message_ar: e.target.value}))} /></div>
                </div>
              </div>

              {/* Customer */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Customer</p>
                <div><label className="text-xs font-medium text-gray-600">Phone</label><input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" value={invoiceData.customer_phone} onChange={e => setInvoiceData(d => ({...d, customer_phone: e.target.value}))} /></div>
              </div>
            </div>

            {/* Print Fields Selector */}
            <div className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-blue-500">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                Print Fields
              </h3>
              <p className="text-xs text-gray-600 mb-4">Select which sections to include in printed output:</p>

              <div className="grid grid-cols-2 gap-4">
                {/* Format Fields */}
                <div className="pb-4 border-b border-gray-200 col-span-2">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Format</p>
                  <div className="space-y-2">
                    {[
                      { key: 'show_logo', label: 'Logo' },
                      { key: 'show_company_info', label: 'Company Info' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-blue-50">
                        <input type="checkbox" checked={invoicePrintFields[field.key]} onChange={e => setInvoicePrintFields(pf => ({...pf, [field.key]: e.target.checked}))} className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="pb-4 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Invoice</p>
                  <div className="space-y-2">
                    {[
                      { key: 'show_invoice_number', label: 'Invoice #' },
                      { key: 'show_invoice_date', label: 'Date' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-blue-50">
                        <input type="checkbox" checked={invoicePrintFields[field.key]} onChange={e => setInvoicePrintFields(pf => ({...pf, [field.key]: e.target.checked}))} className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Customer */}
                <div className="pb-4 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Customer</p>
                  <div className="space-y-2">
                    {[
                      { key: 'show_customer_name', label: 'Name' },
                      { key: 'show_customer_phone', label: 'Phone' },
                      { key: 'show_customer_address', label: 'Address' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-blue-50">
                        <input type="checkbox" checked={invoicePrintFields[field.key]} onChange={e => setInvoicePrintFields(pf => ({...pf, [field.key]: e.target.checked}))} className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Items */}
                <div className="pb-4 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Items</p>
                  <div className="space-y-2">
                    {[
                      { key: 'show_item_details', label: 'Details' },
                      { key: 'show_prices', label: 'Prices' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-blue-50">
                        <input type="checkbox" checked={invoicePrintFields[field.key]} onChange={e => setInvoicePrintFields(pf => ({...pf, [field.key]: e.target.checked}))} className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="pb-4 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Summary</p>
                  <div className="space-y-2">
                    {[
                      { key: 'show_subtotal', label: 'Subtotal' },
                      { key: 'show_tax', label: 'Tax' },
                      { key: 'show_total', label: 'Total' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-blue-50">
                        <input type="checkbox" checked={invoicePrintFields[field.key]} onChange={e => setInvoicePrintFields(pf => ({...pf, [field.key]: e.target.checked}))} className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="col-span-2">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Additional</p>
                  <div className="space-y-2">
                    {[
                      { key: 'show_payment_status', label: 'Payment Status' },
                      { key: 'show_footer_message', label: 'Footer Message' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center p-2 rounded cursor-pointer hover:bg-blue-50">
                        <input type="checkbox" checked={invoicePrintFields[field.key]} onChange={e => setInvoicePrintFields(pf => ({...pf, [field.key]: e.target.checked}))} className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="ml-3 text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="bg-white rounded-xl shadow-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                Live Preview
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 overflow-x-auto">
                <canvas ref={invoiceCanvasRef} style={{ maxWidth: '100%', height: 'auto' }} className="mx-auto" />
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Changes are reflected in real-time as you edit
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
